import React, { useLayoutEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { getDayJs } from '~/services/TimeService';

import ActionDefinitions from './ActionDefinitions';
import { BookedPredictedSummary } from './BookedPredicted';
import ConfidenceBadge from './ConfidenceBadge';
import DepartureStatus from './DepartureStatus';
import EmptyPrediction from './EmptyPrediction';
import MarginBandChip from './MarginBandChip';
import PeriodCharts from './PeriodCharts';
import SectionHeading from './SectionHeading';
import { breakEvenText, breakEvenUnavailableTooltip } from './breakEvenText';
import { departureActionOrder } from './departureActions';
import { emptyPredictionText } from './emptyPredictionText';
import { engagementChange, engagementDelayed } from './engagement';
import {
  PeriodPoint,
  agg,
  basisValue,
  departureOnTrack,
  departureSeries,
  flagLabels,
  money,
  pct,
  periods,
  tourRows,
} from './logic';
import { bundleTravellerNote, cancellationMoneyNote } from './moneyNotes';
import { actionColour, actionText } from './palette';
import { tcc, tccVariables } from './palette';
import { resetPageScroll } from './scroll';
import { toursPageView, validateToursFilters } from './toursPageAdapter';
import { CommandDeparture, CommandFlag, CostBasis } from './types';

const count = (value: number | null | undefined) => (value == null ? '—' : Math.round(value).toLocaleString('en-AU'));

export default function TourDrillDown({
  rows,
  tourId,
  basis,
  range,
  back,
  sample = false,
  snapshotDate,
  contextFilters,
}: {
  rows: Array<CommandDeparture>;
  tourId: string;
  basis: CostBasis;
  range: { from: string; to: string };
  back: () => void;
  sample?: boolean;
  snapshotDate?: string;
  contextFilters?: import('./logic').CommandFilters;
}) {
  const pageRef = useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = useState(false);
  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    setAtTop(false);
    resetPageScroll(pageRef.current);
    let frame: number;
    const reveal = () => {
      if (resetPageScroll(pageRef.current)) setAtTop(true);
      else frame = requestAnimationFrame(reveal);
    };
    // Confirm synchronously before paint; only retry on a later frame if a scroll owner resists the reset.
    reveal();
    return () => {
      cancelAnimationFrame(frame);
      window.history.scrollRestoration = previous;
    };
  }, [tourId]);
  const [flag, setFlag] = useState<CommandFlag | null>(null);
  const [month, setMonth] = useState<PeriodPoint | null>(null);
  const scoped = month ? rows.filter((d) => d.dep >= month.from && d.dep <= month.to) : rows;
  const a = agg(scoped, basis);
  const tour = tourRows(rows, rows, basis, [])[0];
  const engagementDate = rows.find((d) => d.engagement_snapshot_date_syd)?.engagement_snapshot_date_syd;
  const events = rows.find(
    (d) => d.offer_page_views_28d != null || d.tour_add_to_cart_28d != null || d.tour_checkout_views_28d != null,
  );
  const visible = scoped.filter(
    (d) => !flag || (flag === 'green' ? departureOnTrack(d, basis) : basisValue(d, 'flag', basis) === flag),
  );
  const monthOptions = periods(rows, basis, 'month', range);
  const pageView = toursPageView({
    ready: true,
    sample,
    snapshotDate: snapshotDate ?? '',
    filters: {
      windowDays: 183,
      region: tour?.region ?? 'All',
      onlyAttention: false,
      ...contextFilters,
      tourId,
      basis,
    },
    attentionFlag: null,
    rows: visible,
    data: rows,
    summary: a,
    regions: [...new Set(rows.map((row) => row.region))],
    range,
  });
  const drillView = {
    ...pageView,
    title: tour?.name ?? 'Tour departures',
    page: 'departure-drilldown',
    engagement: {
      scope: 'All departures for this tour, independent of selected month and action',
      period: 'Last 28 days versus previous 28 days',
      snapshotDate: engagementDate ?? null,
      offerPageViews: {
        current: events?.offer_page_views_28d ?? null,
        previous: events?.offer_page_views_prev_28d ?? null,
      },
      addToCart: { current: events?.tour_add_to_cart_28d ?? null, previous: events?.tour_add_to_cart_prev_28d ?? null },
      checkoutPageViews: {
        current: events?.tour_checkout_views_28d ?? null,
        previous: events?.tour_checkout_views_prev_28d ?? null,
      },
    },
    filters: { ...pageView.filters, action: flag ?? 'all', month: month?.key ?? null },
    filterSchema: {
      action: { type: 'enum' as const, values: ['all', ...departureActionOrder] },
      month: { type: 'enum' as const, values: [null, ...monthOptions.map((point) => point.key)] },
    },
    monthOptions: monthOptions.map((point) => ({
      value: point.key,
      label: point.label,
      from: point.from,
      to: point.to,
    })),
    summaryDepartureCount: scoped.length,
    definitions: {
      ...pageView.definitions,
      scope:
        'Summary follows the selected month. visibleDepartures and departureCount additionally follow the selected action. Overview dates, cost basis and tour are inherited and cannot be edited here. month:null and action:all clear the local selections.',
    },
  };
  usePageBridge({
    pageId: 'tours-command-centre',
    view: drillView,
    edit: (changes) => {
      validateToursFilters(changes, drillView.filterSchema, pageView.filters);
      if ('action' in changes) setFlag(changes.action === 'all' ? null : (changes.action as CommandFlag));
      if ('month' in changes) setMonth(monthOptions.find((point) => point.key === changes.month) ?? null);
    },
  });
  return (
    <Container
      ref={pageRef}
      data-drill-ready={atTop}
      maxWidth={false}
      sx={(theme) => ({
        opacity: atTop ? 1 : 0,
        '& > :not(button):not(h6):not([data-level-heading])': {
          animation: atTop ? 'tccDepartureReveal 150ms ease-out' : 'none',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
        '@keyframes tccDepartureReveal': { from: { opacity: 0 }, to: { opacity: 1 } },
        overflowAnchor: 'none',
        py: 2,
        pb: 6,
        '& .MuiButton-root, & .MuiToggleButton-root': { textTransform: 'none' },
        ...tccVariables(theme.palette.mode),
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Button onClick={back} variant="outlined" size="large" sx={{ borderRadius: 10, px: 3, mb: 2 }}>
          ← Back to All Tours
        </Button>
        <PageChatButton />
      </Box>
      <Typography variant="h6">Departure Drill-Down Dashboard</Typography>
      <SectionHeading
        title={`Tour Level: ${tour?.name || tourId}`}
        subtitle="Summary, tour engagement and performance by departure date."
      />
      <Typography color="text.secondary">
        {tour?.region} · {tour?.country || '—'}
      </Typography>
      {!tour && <Alert severity="info">No departures for this tour in the current date window and filters.</Alert>}
      <Box
        data-testid="command-centre-summary"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5, my: 2 }}
      >
        <BookedPredictedSummary a={a} />
      </Box>
      <Card variant="outlined" sx={{ p: 2, my: 2 }} data-testid="tour-engagement">
        <Typography
          variant="subtitle2"
          sx={{ bgcolor: 'action.hover', borderLeft: '4px solid', borderColor: 'divider', p: 1, fontWeight: 700 }}
        >
          Tour-Level Online Interactions (Last 28 Days, All Departures)
        </Typography>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2, my: 1 }}
        >
          {[
            ['Offer Page Views', events?.offer_page_views_28d, events?.offer_page_views_prev_28d],
            ['Add to Cart', events?.tour_add_to_cart_28d, events?.tour_add_to_cart_prev_28d],
            ['Checkout Page Views', events?.tour_checkout_views_28d, events?.tour_checkout_views_prev_28d],
          ].map(([label, current, previous]) => (
            <Box key={String(label)}>
              <Typography variant="body2">{label}</Typography>
              <Typography variant="h4">{count(current as number)}</Typography>
              <Typography variant="caption">{engagementChange(current as number, previous as number)}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" display="block">
          Data as of {engagementDate ? getDayJs(engagementDate).format('D MMM YYYY') : 'Unavailable'}
        </Typography>
        {engagementDate &&
          engagementDelayed(
            sample,
            getDayJs().tz('Australia/Sydney').startOf('day').diff(getDayJs(engagementDate), 'day'),
          ) && (
            <Typography variant="caption" color="text.secondary">
              Data delayed
            </Typography>
          )}
      </Card>
      {month && <Chip label={`Departures: ${month.label}`} onDelete={() => setMonth(null)} />}
      <PeriodCharts
        points={periods(rows, basis, 'month', range)}
        departurePoints={departureSeries(rows, basis)}
        basis={basis}
        tour={tour}
        tours={[]}
        showTourPicker={false}
        onTourChange={() => undefined}
        selectionLabel={null}
        unit="departure"
        onUnitChange={() => undefined}
        selected={month?.key || null}
        onSelect={setMonth}
        onDepartureSelect={() => undefined}
      />
      <SectionHeading
        title="Departure Level"
        subtitle={`Departures: ${visible.length}. Filter departures by action below.`}
        accent={tcc.predicted}
      />
      <Typography variant="subtitle2">Departure Action</Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', my: 1 }}>
        <Chip label="Departures: All" onClick={() => setFlag(null)} variant={flag ? 'outlined' : 'filled'} />
        {departureActionOrder.map((f) => (
          <Chip
            key={f}
            label={`Departures: ${flagLabels[f]} (${scoped.filter((d) => basisValue(d, 'flag', basis) === f).length})`}
            sx={{ color: actionText(f), bgcolor: alpha(actionColour(f), 0.14) }}
            onClick={() => setFlag(flag === f ? null : f)}
            variant={flag === f ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>
      <ActionDefinitions />
      <TableContainer component={Card} variant="outlined">
        <Table
          size="small"
          aria-label="Departure drill-down table"
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& td': { verticalAlign: 'top', fontVariantNumeric: 'tabular-nums', fontSize: 12, px: 0.5, py: 1 },
            '& th': {
              whiteSpace: 'normal',
              overflowWrap: 'normal',
              wordBreak: 'normal',
              px: 0.5,
              fontSize: 12,
              bgcolor: tcc.booked,
              color: '#fff',
            },
            '@media (max-width: 767px)': {
              '&, & tbody': { display: 'block' },
              '& thead': { display: 'none' },
              '& tbody tr': {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& td': { display: 'block', minWidth: 0, borderBottom: 0, px: 1 },
              '& td::before': {
                content: 'attr(data-label)',
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                mb: 0.5,
              },
              '& td:first-of-type, & td:last-of-type': { gridColumn: '1 / -1' },
            },
          }}
        >
          <TableHead>
            <TableRow>
              {[
                'Departure Date',
                'Status',
                'Load Factor',
                'Predicted Load Factor',
                'Confidence',
                'Current Booked Numbers',
                'Predicted at Handback',
                'Departure Action',
                'Online Interactions: Last 28 Days',
              ].map((label, index) => (
                <TableCell key={label} sx={{ width: [11, 9, 6, 13, 12, 11, 11, 9, 18][index] + '%' }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...visible]
              .sort((x, y) => x.dep.localeCompare(y.dep))
              .map((d) => (
                <TableRow key={d.dep_id}>
                  <TableCell data-label="Departure Date">
                    {getDayJs(d.dep).format('D MMM YYYY')}
                    <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>
                      Handback {getDayJs(d.handback).format('D MMM YYYY')}
                    </Typography>
                  </TableCell>
                  <TableCell data-label="Status">
                    <DepartureStatus departure={d} />
                    {d.bundle_pax > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {bundleTravellerNote(d)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell data-label="Load Factor">
                    <Chip
                      size="small"
                      label={pct(d.lf)}
                      sx={{ bgcolor: alpha(tcc.booked, 0.1), color: tcc.bookedText }}
                    />
                    <Typography variant="caption" display="block">
                      {d.pax}{' '}
                      <Box component="span" data-seats-text sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                        of {d.max_pax} seats
                      </Box>
                    </Typography>
                  </TableCell>
                  <TableCell data-label="Predicted Load Factor">
                    {emptyPredictionText(d) ? (
                      <EmptyPrediction departure={d} />
                    ) : (
                      <>
                        <Tooltip title={`P10–P90: ${count(d.pred_pax_p10)}–${count(d.pred_pax_p90)} travellers`}>
                          <Chip
                            size="small"
                            label={pct(d.pred_lf)}
                            sx={{ bgcolor: alpha(tcc.predicted, 0.15), color: tcc.predictedText }}
                          />
                        </Tooltip>
                        <Typography variant="caption" display="block">
                          {count(d.pred_pax)}{' '}
                          <Box component="span" data-seats-text sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                            of {d.max_pax} seats
                          </Box>
                        </Typography>
                      </>
                    )}
                    <Tooltip title={d[`breakeven_pax_${basis}`] == null ? breakEvenUnavailableTooltip : ''}>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        tabIndex={d[`breakeven_pax_${basis}`] == null ? 0 : undefined}
                        data-breakeven
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {breakEvenText(d, basis)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell data-label="Confidence" sx={{ pr: 1.5 }}>
                    <ConfidenceBadge
                      tier={d.pred_confidence_tier}
                      capacity={d.max_pax}
                      source={d.pred_pax_source}
                      score={d.pred_confidence}
                    />
                  </TableCell>
                  <TableCell data-label="Current Booked Numbers" sx={{ bgcolor: alpha(tcc.booked, 0.06) }}>
                    <Box data-testid="departure-metric-value">TTV {money(d.ttv)}</Box>
                    <Tooltip title={cancellationMoneyNote(d) || ''}>
                      <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>
                        Margin{' '}
                        {d[`margin_bands_${basis}`]?.margin_available && d[`margin_pct_${basis}`] != null
                          ? `${money(d[`le_margin_${basis}`])} · ${pct(d[`margin_pct_${basis}`])}`
                          : '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell data-label="Predicted at Handback" sx={{ bgcolor: alpha(tcc.predicted, 0.12) }}>
                    <Box data-testid="departure-metric-value">TTV {d.pred_ttv == null ? '—' : money(d.pred_ttv)}</Box>
                    <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>
                      Margin {d[`pred_margin_${basis}`] == null ? '—' : money(d[`pred_margin_${basis}`])} ·{' '}
                      {pct(d[`pred_margin_pct_${basis}`])}
                    </Typography>
                  </TableCell>
                  <TableCell data-label="Departure Action">
                    <MarginBandChip departure={d} basis={basis} showConfidence={false} />
                  </TableCell>
                  <TableCell data-label="Online Interactions: Last 28 Days">
                    <Box
                      data-interaction-tiles
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: 0.5,
                        width: '100%',
                      }}
                    >
                      {[
                        ['Add to Cart', d.add_to_cart_28d],
                        ['Offer Page Views (Date)', d.booking_flow_views_28d],
                        ['Checkout Page Views', d.checkout_views_28d],
                      ].map(([label, value]) => (
                        <Box
                          key={String(label)}
                          sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 0.5, textAlign: 'center', minWidth: 0 }}
                        >
                          <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {count(value as number | null)}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
                            {label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button onClick={back} variant="outlined" size="large" sx={{ borderRadius: 10, px: 3, mt: 3 }}>
        ← Back to All Tours
      </Button>
    </Container>
  );
}
